
import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { FinanceRecord } from '../../../store/financeStore';

const MONTHS = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];

interface Props {
  records: FinanceRecord[];
  year?: string;
}

export default function AnnualChart({ records, year = '2026' }: Props) {
  const chartData = useMemo(() => {
    const monthlyMap: Record<number, { purchase: number; sales: number; purchaseAmt: number; salesAmt: number }> = {};
    for (let m = 1; m <= 12; m++) {
      monthlyMap[m] = { purchase: 0, sales: 0, purchaseAmt: 0, salesAmt: 0 };
    }

    records.forEach(r => {
      if (!r.date.startsWith(year)) return;
      const month = parseInt(r.date.split('-')[1], 10);
      if (!monthlyMap[month]) return;
      if (r.bizType === '收购') {
        monthlyMap[month].purchase += r.quantity;
        monthlyMap[month].purchaseAmt += r.amount;
      } else {
        monthlyMap[month].sales += r.quantity;
        monthlyMap[month].salesAmt += r.amount;
      }
    });

    return MONTHS.map((label, idx) => {
      const m = idx + 1;
      const d = monthlyMap[m];
      return {
        name: label,
        收购量: d.purchase,
        销售量: d.sales,
        收购金额: Math.round(d.purchaseAmt / 10000),
        销售金额: Math.round(d.salesAmt / 10000),
      };
    });
  }, [records, year]);

  const totals = useMemo(() => {
    return chartData.reduce((acc, d) => ({
      purchase: acc.purchase + d['收购量'],
      sales: acc.sales + d['销售量'],
      purchaseAmt: acc.purchaseAmt + d['收购金额'],
      salesAmt: acc.salesAmt + d['销售金额'],
    }), { purchase: 0, sales: 0, purchaseAmt: 0, salesAmt: 0 });
  }, [chartData]);

  const hasData = totals.purchase > 0 || totals.sales > 0;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
          <i className="ri-line-chart-line mr-2 text-[#0c7362]"></i>
          年度经营数据趋势
        </h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3 text-xs">
            <span className="flex items-center"><span className="w-3 h-3 rounded-full bg-[#0c7362] mr-1.5"></span>收购量(吨)</span>
            <span className="flex items-center"><span className="w-3 h-3 rounded-full bg-[#f59e0b] mr-1.5"></span>销售量(吨)</span>
          </div>
        </div>
      </div>

      {/* 汇总指标 */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: '年度收购', value: `${totals.purchase.toLocaleString()}`, unit: '吨', color: 'text-[#0c7362]', bg: 'bg-[#0c7362]/5' },
          { label: '年度销售', value: `${totals.sales.toLocaleString()}`, unit: '吨', color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: '收购金额', value: `${totals.purchaseAmt.toLocaleString()}`, unit: '万元', color: 'text-[#0c7362]', bg: 'bg-[#0c7362]/5' },
          { label: '销售金额', value: `${totals.salesAmt.toLocaleString()}`, unit: '万元', color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(item => (
          <div key={item.label} className={`${item.bg} rounded-xl px-4 py-3`}>
            <div className="text-xs text-gray-500 mb-1">{item.label}</div>
            <div className={`text-lg font-bold ${item.color}`}>
              {item.value}
              <span className="text-xs font-normal text-gray-400 ml-1">{item.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* 图表 */}
      {hasData ? (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="purchaseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0c7362" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0c7362" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  fontSize: '13px',
                  padding: '12px 16px',
                }}
                formatter={(value: number, name: string) => [`${value.toLocaleString()} ${name.includes('金额') ? '万元' : '吨'}`, name]}
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: '12px', color: '#6b7280' }}
              />
              <Area type="monotone" dataKey="收购量" stroke="#0c7362" strokeWidth={2.5} fill="url(#purchaseGrad)" dot={{ r: 4, fill: '#0c7362', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, fill: '#0c7362', strokeWidth: 2, stroke: '#fff' }} />
              <Area type="monotone" dataKey="销售量" stroke="#f59e0b" strokeWidth={2.5} fill="url(#salesGrad)" dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[300px] flex flex-col items-center justify-center text-gray-300 bg-gray-50 rounded-xl">
          <i className="ri-line-chart-line text-4xl mb-3"></i>
          <p className="text-sm text-gray-400">{year}年暂无经营数据</p>
          <p className="text-xs text-gray-300 mt-1">录入数据后将自动生成趋势图</p>
        </div>
      )}
    </div>
  );
}
